import { supabase } from '../lib/supabase';
import { buildVapiToolConfig } from './toolConfigService';
import { syncAssistantToRetell } from './retellSyncService';
import { buildVapiVoiceConfig } from './vapiVoiceConfig';

/**
 * Assistant Configuration Service
 * Manages Vapi assistant configurations for Coach Assistants
 * Super Admin only (for CRUD), All users can read for calls
 */

/**
 * Replaces {{customer_name}}, {{customer_full_name}} and {{coach_name}} with
 * user data, or removes them when the data is missing. Whitespace is kept
 * as is, so this is the helper for multi-line prompts.
 * @param {string} message - The message template
 * @param {object} userData - User profile data containing first_name, last_name, etc.
 * @returns {string} Message with placeholders filled
 */
export const substitutePlaceholders = (message, userData = {}) => {
  if (!message || typeof message !== 'string') {
    return message;
  }

  const u = userData || {};
  const fullName = u.full_name || [u.first_name, u.last_name].filter(Boolean).join(' ');

  return message
    .replace(/\{\{customer_name\}\}/g, u.first_name || '')
    .replace(/\{\{customer_full_name\}\}/g, fullName || '')
    .replace(/\{\{coach_name\}\}/g, u.coach_name || '');
};

/**
 * Personalizes a spoken line (the greeting): fills placeholders, then
 * collapses whitespace. Never use it on a prompt; it flattens newlines.
 * @param {string} message - The message template with variables like {{customer_name}}
 * @param {object} userData - User profile data containing first_name, last_name, etc.
 * @returns {string} Personalized message
 */
const personalizeMessage = (message, userData = {}) => {
  const personalizedMessage = substitutePlaceholders(message, userData);
  if (!personalizedMessage || typeof personalizedMessage !== 'string') {
    return personalizedMessage;
  }

  // Clean up any double spaces that might result from variable removal
  return personalizedMessage.replace(/\s+/g, ' ').trim();
};

/**
 * Get all assistants with their tools
 */
export const getAssistants = async (options = {}) => {
  try {
    const {
      search = '',
      isActive = null,
      coachId = null,
      sortBy = 'created_at',
      sortOrder = 'desc'
    } = options;

    let query = supabase
      .from('vapi_assistants')
      .select('*');

    // Apply search filter
    if (search.trim()) {
      query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
    }

    // Apply active filter
    if (isActive !== null) {
      query = query.eq('is_active', isActive);
    }

    // Apply coach filter
    if (coachId !== null) {
      query = query.eq('coach_id', coachId);
    }

    // Apply sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching assistants:', error);
      return { data: null, error: error.message };
    }

    // Fetch tools for each assistant
    const assistantsWithTools = await Promise.all(
      data.map(async (assistant) => {
        const { data: toolRelations, error: toolError } = await supabase
          .from('vapi_assistant_tools')
          .select(`
            tool_order,
            tool:vapi_tools(*)
          `)
          .eq('assistant_id', assistant.id)
          .order('tool_order', { ascending: true });

        if (toolError) {
          console.error('Error fetching tools for assistant:', toolError);
          return { ...assistant, tools: [] };
        }

        return {
          ...assistant,
          tools: toolRelations.map(rel => rel.tool)
        };
      })
    );

    return { data: assistantsWithTools, error: null };
  } catch (err) {
    console.error('Error in getAssistants:', err);
    return { data: null, error: err.message };
  }
};

/**
 * Get a single assistant by ID with its tools
 */
export const getAssistantById = async (assistantId) => {
  try {
    const { data: assistant, error } = await supabase
      .from('vapi_assistants')
      .select('*')
      .eq('id', assistantId)
      .single();

    if (error) {
      console.error('Error fetching assistant:', error);
      return { data: null, error: error.message };
    }

    // Fetch tools for this assistant
    const { data: toolRelations, error: toolError } = await supabase
      .from('vapi_assistant_tools')
      .select(`
        tool_order,
        tool:vapi_tools(*)
      `)
      .eq('assistant_id', assistantId)
      .order('tool_order', { ascending: true });

    if (toolError) {
      console.error('Error fetching tools for assistant:', toolError);
      return { data: assistant, error: null };
    }

    return {
      data: {
        ...assistant,
        tools: toolRelations.map(rel => rel.tool)
      },
      error: null
    };
  } catch (err) {
    console.error('Error in getAssistantById:', err);
    return { data: null, error: err.message };
  }
};

/**
 * Get the appropriate assistant for a user (uses database function)
 */
export const getUserAssistant = async (userId) => {
  try {
    const { data: assistantId, error } = await supabase
      .rpc('get_user_assistant', { user_id_param: userId });

    if (error) {
      console.error('Error getting user assistant:', error);
      return { data: null, error: error.message };
    }

    if (!assistantId) {
      return { data: null, error: 'No assistant configured' };
    }

    // Fetch the full assistant with tools
    return await getAssistantById(assistantId);
  } catch (err) {
    console.error('Error in getUserAssistant:', err);
    return { data: null, error: err.message };
  }
};

/**
 * Create a new assistant
 */
export const createAssistant = async (assistantData) => {
  try {
    const {
      name,
      description,
      is_default = false,
      is_trial = false,
      trial_call_limit = null,
      coach_id = null,
      // Voice config
      voice_provider = 'vapi',
      voice_id = 'Lily',
      voice_model,
      voice_speed = 1.0,
      voice_stability,
      voice_similarity_boost,
      voice_volume,
      retell_voice_id = '',
      retell_voice_model,
      retell_voice_speed,
      retell_volume,
      retell_voice_temperature,
      retell_voice_emotion,
      retell_model = '',
      retell_responsiveness = '',
      retell_interruption_sensitivity = '',
      retell_dynamic_responsiveness = null,
      retell_end_call_silence_seconds = '',
      retell_reminder_trigger_seconds = '',
      retell_reminder_max_count = '',
      retell_enable_backchannel = null,
      retell_backchannel_frequency = '',
      // Model config
      model_provider = 'openai',
      model_name = 'gpt-4o-mini',
      model_temperature = 0.7,
      model_max_tokens,
      system_message,
      // Transcriber config
      transcriber_provider = 'deepgram',
      transcriber_model = 'nova-2',
      transcriber_language = 'en',
      // Messages
      first_message,
      voicemail_message,
      end_call_message,
      // Call settings
      end_call_function_enabled = true,
      silence_timeout_seconds = 90,
      max_duration_seconds = 1800,
      background_sound = 'off',
      background_denoising_enabled = true,
      // Speaking plans
      start_speaking_wait_seconds = 1.2,
      start_speaking_on_punctuation_seconds = 1.5,
      start_speaking_on_no_punctuation_seconds = 3.0,
      start_speaking_on_number_seconds = 0.8,
      stop_speaking_num_words = 1,
      stop_speaking_backoff_seconds = 4.0,
      // Server config
      server_url,
      server_timeout_seconds = 20,
      server_messages = [],
      client_messages = [],
      analysis_plan = {},
      // Tools
      tool_ids = [],
      is_active = true,
      hooks = []
    } = assistantData;

    // Helper to convert empty strings to null for optional fields
    const toNullIfEmpty = (value) => value === '' || value === undefined ? null : value;
    const toNullIfEmptyNum = (value) => value === '' || value === undefined || value === null ? null : value;

    // If this assistant is being assigned to a specific coach, deactivate any other
    // active assistants for that coach so the resolver always returns the newest one.
    const resolvedCoachId = toNullIfEmpty(coach_id);
    if (resolvedCoachId) {
      const { error: deactivateError } = await supabase
        .from('vapi_assistants')
        .update({ is_active: false })
        .eq('coach_id', resolvedCoachId)
        .eq('is_active', true);

      if (deactivateError) {
        console.error('Error deactivating prior coach assistants:', deactivateError);
        // Non-fatal: proceed with insert; the RPC ORDER BY will still pick the newest
      } else {
        console.log('✅ Deactivated prior active assistants for coach:', resolvedCoachId);
      }
    }

    const { data: assistant, error } = await supabase
      .from('vapi_assistants')
      .insert({
        name,
        description: toNullIfEmpty(description),
        is_default,
        is_trial,
        trial_call_limit: is_trial ? (trial_call_limit || 1) : null,
        coach_id: resolvedCoachId,
        voice_provider,
        voice_id,
        voice_model: toNullIfEmpty(voice_model),
        voice_speed,
        voice_stability: toNullIfEmptyNum(voice_stability),
        voice_similarity_boost: toNullIfEmptyNum(voice_similarity_boost),
        voice_volume: toNullIfEmptyNum(voice_volume),
        retell_voice_id: toNullIfEmpty(retell_voice_id),
        retell_voice_model: toNullIfEmpty(retell_voice_model),
        retell_voice_speed: toNullIfEmptyNum(retell_voice_speed),
        retell_volume: toNullIfEmptyNum(retell_volume),
        retell_voice_temperature: toNullIfEmptyNum(retell_voice_temperature),
        retell_voice_emotion: toNullIfEmpty(retell_voice_emotion),
        retell_model: toNullIfEmpty(retell_model),
        // Blank = not set for this coach; the edge function falls back to the
        // deployment default and then to Retell's own default.
        retell_responsiveness: toNullIfEmptyNum(retell_responsiveness),
        retell_interruption_sensitivity: toNullIfEmptyNum(retell_interruption_sensitivity),
        retell_dynamic_responsiveness,
        retell_end_call_silence_seconds: toNullIfEmptyNum(retell_end_call_silence_seconds),
        retell_reminder_trigger_seconds: toNullIfEmptyNum(retell_reminder_trigger_seconds),
        retell_reminder_max_count: toNullIfEmptyNum(retell_reminder_max_count),
        retell_enable_backchannel,
        retell_backchannel_frequency: toNullIfEmptyNum(retell_backchannel_frequency),
        model_provider,
        model_name,
        model_temperature,
        model_max_tokens: toNullIfEmptyNum(model_max_tokens),
        system_message,
        transcriber_provider,
        transcriber_model,
        transcriber_language,
        first_message: toNullIfEmpty(first_message),
        voicemail_message: toNullIfEmpty(voicemail_message),
        end_call_message: toNullIfEmpty(end_call_message),
        end_call_function_enabled,
        silence_timeout_seconds,
        max_duration_seconds,
        background_sound,
        background_denoising_enabled,
        start_speaking_wait_seconds,
        start_speaking_on_punctuation_seconds,
        start_speaking_on_no_punctuation_seconds,
        start_speaking_on_number_seconds,
        stop_speaking_num_words,
        stop_speaking_backoff_seconds,
        server_url: toNullIfEmpty(server_url),
        server_timeout_seconds,
        server_messages,
        client_messages,
        analysis_plan,
        is_active,
        hooks,
        created_by: (await supabase.auth.getUser()).data.user?.id
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating assistant:', error);
      return { data: null, error: error.message };
    }

    // Associate tools if provided
    if (tool_ids && tool_ids.length > 0) {
      const toolRelations = tool_ids.map((toolId, index) => ({
        assistant_id: assistant.id,
        tool_id: toolId,
        tool_order: index
      }));

      const { error: toolError } = await supabase
        .from('vapi_assistant_tools')
        .insert(toolRelations);

      if (toolError) {
        console.error('Error associating tools:', toolError);
        // Don't fail the entire operation
      }
    }

    // Push the new row to Retell (per-coach LLM + agent). A sync failure must
    // never fail the save: the row above is already committed, so log the
    // failure and surface it on the returned record for anything that reads
    // it immediately, without a DB refetch (the edge function itself also
    // persists retell_sync_error on the row for later reads).
    const syncResult = await syncAssistantToRetell(assistant.id);
    if (syncResult.error) {
      console.error('Retell sync failed for new assistant', assistant.id, syncResult.error);
    }

    return { data: { ...assistant, retell_sync_error: syncResult.error || null }, error: null };
  } catch (err) {
    console.error('Error in createAssistant:', err);
    return { data: null, error: err.message };
  }
};

/**
 * Update an existing assistant
 */
export const updateAssistant = async (assistantId, assistantData) => {
  try {
    const { tool_ids, ...rawUpdateData } = assistantData;

    // Helper to convert empty strings to null for optional fields
    const toNullIfEmpty = (value) => value === '' || value === undefined ? null : value;
    const toNullIfEmptyNum = (value) => value === '' || value === undefined || value === null ? null : value;

    // Clean up the update data - convert empty strings to null for numeric/optional fields
    const updateData = { ...rawUpdateData };
    if (updateData.description !== undefined) updateData.description = toNullIfEmpty(updateData.description);
    if (updateData.coach_id !== undefined) updateData.coach_id = toNullIfEmpty(updateData.coach_id);
    // Only set trial_call_limit if is_trial is true, otherwise null it out
    if (updateData.is_trial !== undefined) {
      updateData.trial_call_limit = updateData.is_trial ? (updateData.trial_call_limit || 1) : null;
    }
    if (updateData.voice_model !== undefined) updateData.voice_model = toNullIfEmpty(updateData.voice_model);
    if (updateData.voice_stability !== undefined) updateData.voice_stability = toNullIfEmptyNum(updateData.voice_stability);
    if (updateData.voice_similarity_boost !== undefined) updateData.voice_similarity_boost = toNullIfEmptyNum(updateData.voice_similarity_boost);
    if (updateData.voice_volume !== undefined) updateData.voice_volume = toNullIfEmptyNum(updateData.voice_volume);
    if (updateData.retell_voice_id !== undefined) updateData.retell_voice_id = toNullIfEmpty(updateData.retell_voice_id);
    if (updateData.retell_voice_model !== undefined) updateData.retell_voice_model = toNullIfEmpty(updateData.retell_voice_model);
    if (updateData.retell_voice_speed !== undefined) updateData.retell_voice_speed = toNullIfEmptyNum(updateData.retell_voice_speed);
    if (updateData.retell_volume !== undefined) updateData.retell_volume = toNullIfEmptyNum(updateData.retell_volume);
    if (updateData.retell_voice_temperature !== undefined) updateData.retell_voice_temperature = toNullIfEmptyNum(updateData.retell_voice_temperature);
    if (updateData.retell_voice_emotion !== undefined) updateData.retell_voice_emotion = toNullIfEmpty(updateData.retell_voice_emotion);
    if (updateData.retell_model !== undefined) updateData.retell_model = toNullIfEmpty(updateData.retell_model);
    // Blank numeric dials must clear the column, not be sent as '' (a 22P02 error).
    for (const f of [
      'retell_responsiveness', 'retell_interruption_sensitivity', 'retell_end_call_silence_seconds',
      'retell_reminder_trigger_seconds', 'retell_reminder_max_count', 'retell_backchannel_frequency',
    ]) {
      if (updateData[f] !== undefined) updateData[f] = toNullIfEmptyNum(updateData[f]);
    }
    if (updateData.model_max_tokens !== undefined) updateData.model_max_tokens = toNullIfEmptyNum(updateData.model_max_tokens);
    if (updateData.first_message !== undefined) updateData.first_message = toNullIfEmpty(updateData.first_message);
    if (updateData.voicemail_message !== undefined) updateData.voicemail_message = toNullIfEmpty(updateData.voicemail_message);
    if (updateData.end_call_message !== undefined) updateData.end_call_message = toNullIfEmpty(updateData.end_call_message);
    if (updateData.server_url !== undefined) updateData.server_url = toNullIfEmpty(updateData.server_url);
    // Ensure arrays are never undefined
    if (updateData.server_messages !== undefined && !Array.isArray(updateData.server_messages)) updateData.server_messages = [];
    if (updateData.client_messages !== undefined && !Array.isArray(updateData.client_messages)) updateData.client_messages = [];
    if (updateData.hooks !== undefined && !Array.isArray(updateData.hooks)) updateData.hooks = [];

    // Update assistant record
    const { data: assistant, error } = await supabase
      .from('vapi_assistants')
      .update(updateData)
      .eq('id', assistantId)
      .select()
      .single();

    if (error) {
      console.error('Error updating assistant:', error);
      return { data: null, error: error.message };
    }

    // Update tool associations if provided
    if (tool_ids !== undefined) {
      // First, remove all existing tool associations
      await supabase
        .from('vapi_assistant_tools')
        .delete()
        .eq('assistant_id', assistantId);

      // Then add the new associations
      if (tool_ids.length > 0) {
        const toolRelations = tool_ids.map((toolId, index) => ({
          assistant_id: assistantId,
          tool_id: toolId,
          tool_order: index
        }));

        const { error: toolError } = await supabase
          .from('vapi_assistant_tools')
          .insert(toolRelations);

        if (toolError) {
          console.error('Error updating tool associations:', toolError);
        }
      }
    }

    // Push the update to Retell (per-coach LLM + agent). Same rule as
    // createAssistant: never fail the save over a sync failure -- log it and
    // surface it on the returned record (the edge function also persists
    // retell_sync_error on the row itself for later reads).
    const syncResult = await syncAssistantToRetell(assistant.id);
    if (syncResult.error) {
      console.error('Retell sync failed for assistant', assistant.id, syncResult.error);
    }

    return { data: { ...assistant, retell_sync_error: syncResult.error || null }, error: null };
  } catch (err) {
    console.error('Error in updateAssistant:', err);
    return { data: null, error: err.message };
  }
};

/**
 * Delete an assistant
 */
export const deleteAssistant = async (assistantId) => {
  try {
    const { error } = await supabase
      .from('vapi_assistants')
      .delete()
      .eq('id', assistantId);

    if (error) {
      console.error('Error deleting assistant:', error);
      return { success: false, error: error.message };
    }

    return { success: true, error: null };
  } catch (err) {
    console.error('Error in deleteAssistant:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Toggle assistant active status
 */
export const toggleAssistantStatus = async (assistantId, isActive) => {
  try {
    const { data, error } = await supabase
      .from('vapi_assistants')
      .update({ is_active: isActive })
      .eq('id', assistantId)
      .select()
      .single();

    if (error) {
      console.error('Error toggling assistant status:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Error in toggleAssistantStatus:', err);
    return { data: null, error: err.message };
  }
};

/**
 * Build Vapi transient assistant configuration
 * Converts DB format to Vapi SDK format for calls
 * Based on existing assistantService.buildVapiCallConfig pattern
 * @param {object} assistantRecord - The assistant configuration from database
 * @param {object} userData - User profile data for personalization (optional)
 */
export const buildVapiCallConfig = async (assistantRecord, userData = null) => {
  try {
    console.log('🔧 Building Vapi call config for assistant:', assistantRecord.name);

    // Build voice configuration
    const voiceConfig = buildVapiVoiceConfig(assistantRecord);

    // Build model configuration
    const modelConfig = {
      provider: assistantRecord.model_provider || 'openai',
      model: assistantRecord.model_name || 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: substitutePlaceholders(assistantRecord.system_message, userData)
        }
      ]
    };

    // Add optional model parameters
    if (assistantRecord.model_temperature) modelConfig.temperature = parseFloat(assistantRecord.model_temperature);
    if (assistantRecord.model_max_tokens) modelConfig.maxTokens = assistantRecord.model_max_tokens;

    // Build transcriber configuration
    const transcriberConfig = {
      provider: assistantRecord.transcriber_provider || 'deepgram',
      model: assistantRecord.transcriber_model || 'nova-2',
      language: assistantRecord.transcriber_language || 'en'
    };

    // Build the complete Vapi configuration
    const config = {
      name: assistantRecord.name,
      transcriber: transcriberConfig,
      model: modelConfig,
      voice: voiceConfig,
      firstMessage: personalizeMessage(assistantRecord.first_message, userData),
      voicemailMessage: assistantRecord.voicemail_message,
      endCallMessage: assistantRecord.end_call_message,
      endCallFunctionEnabled: assistantRecord.end_call_function_enabled ?? true,
      silenceTimeoutSeconds: assistantRecord.silence_timeout_seconds || 90,
      maxDurationSeconds: assistantRecord.max_duration_seconds || 1800,
      backgroundSound: assistantRecord.background_sound || 'off',
      backgroundDenoisingEnabled: assistantRecord.background_denoising_enabled ?? true,
      serverMessages: assistantRecord.server_messages || [],
      clientMessages: assistantRecord.client_messages || []
    };

    // Add tools at TOP LEVEL (not in model) if assistant has tools
    if (assistantRecord.tools && assistantRecord.tools.length > 0) {
      config.model.tools = assistantRecord.tools.map(tool => buildVapiToolConfig(tool));
      console.log(`📦 Added ${config.model.tools.length} tools to assistant config`);
      console.log('📦 Tool configs:', JSON.stringify(config.model.tools, null, 2));
    }

    // Add start speaking plan
    if (assistantRecord.start_speaking_wait_seconds) {
      config.startSpeakingPlan = {
        waitSeconds: parseFloat(assistantRecord.start_speaking_wait_seconds),
        transcriptionEndpointingPlan: {
          onPunctuationSeconds: parseFloat(assistantRecord.start_speaking_on_punctuation_seconds || 1.5),
          onNoPunctuationSeconds: parseFloat(assistantRecord.start_speaking_on_no_punctuation_seconds || 3.0),
          onNumberSeconds: parseFloat(assistantRecord.start_speaking_on_number_seconds || 0.8)
        }
      };
    }

    // Add stop speaking plan
    if (assistantRecord.stop_speaking_num_words) {
      config.stopSpeakingPlan = {
        numWords: assistantRecord.stop_speaking_num_words,
        backoffSeconds: parseFloat(assistantRecord.stop_speaking_backoff_seconds || 4.0)
      };
    }

    // Add server configuration if provided
    if (assistantRecord.server_url) {
      config.server = {
        url: assistantRecord.server_url,
        timeoutSeconds: assistantRecord.server_timeout_seconds || 20
      };
    }

    // Add analysis plan if provided
    if (assistantRecord.analysis_plan && Object.keys(assistantRecord.analysis_plan).length > 0) {
      config.analysisPlan = assistantRecord.analysis_plan;
    }

    // Add hooks if present (for idle messages)
    if (assistantRecord.hooks && assistantRecord.hooks.length > 0) {
      config.hooks = assistantRecord.hooks;
    }

    console.log('✅ Built Vapi transient assistant config');
    return config;
  } catch (error) {
    console.error('❌ Error building Vapi config:', error);
    throw new Error(`Failed to build call configuration: ${error.message}`);
  }
};

export default {
  getAssistants,
  getAssistantById,
  getUserAssistant,
  createAssistant,
  updateAssistant,
  deleteAssistant,
  toggleAssistantStatus,
  buildVapiCallConfig
};